import { useEffect, useState } from "react";
import axios from "axios";
import {
  Plus,
  X,
  Camera,
  Trash2,
  Image as ImageIcon,
  Home,
  TreePine,
  Dumbbell,
  LayoutGrid,
  Activity,
} from "lucide-react";
import { useAuth } from "@/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "todas", label: "Todas", icon: LayoutGrid },
  { id: "exterior", label: "Exterior", icon: TreePine },
  { id: "interior", label: "Interior", icon: Home },
  { id: "cardio", label: "Cardio", icon: Activity },
  { id: "equipamento", label: "Equipamento", icon: Dumbbell },
  { id: "geral", label: "Geral", icon: ImageIcon },
];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function UploadModal({ onSaved, onClose }) {
  const [form, setForm] = useState({ foto: "", titulo: "", categoria: "geral" });
  const [saving, setSaving] = useState(false);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      alert("A foto deve ter menos de 3 MB.");
      return;
    }
    const url = await fileToDataUrl(f);
    setForm((s) => ({ ...s, foto: url }));
  };

  const submit = async () => {
    if (!form.foto) {
      alert("Carrega uma foto primeiro.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/studio/photos`, form);
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Erro ao guardar foto.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="studio-photo-modal"
    >
      <div className="bg-neutral-950 border border-neutral-800 max-w-xl w-full p-6 md:p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl uppercase tracking-tighter">
            Nova foto · galeria do estúdio
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-yellow-400">
            <X size={22} />
          </button>
        </div>

        <label className="block border border-neutral-800 hover:border-yellow-400 cursor-pointer relative overflow-hidden bg-neutral-950 aspect-video">
          {form.foto ? (
            <img src={form.foto} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500">
              <Camera size={40} />
              <span className="text-xs uppercase tracking-widest mt-2">
                Carregar foto (até 3 MB)
              </span>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={onPickFile}
            className="hidden"
            data-testid="studio-photo-file"
          />
        </label>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-2">
            Título (opcional)
          </div>
          <input
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            className="field"
            placeholder="Ex.: Zona interior · vista geral"
            data-testid="studio-photo-titulo"
          />
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-2">
            Categoria
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.filter((c) => c.id !== "todas").map((c) => (
              <button
                key={c.id}
                type="button"
                className="chip"
                data-active={form.categoria === c.id}
                onClick={() => setForm({ ...form, categoria: c.id })}
                data-testid={`studio-cat-${c.id}`}
              >
                <c.icon size={12} /> {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-primary"
            data-testid="studio-photo-save"
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GaleriaView() {
  const { authed } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState("todas");
  const [showForm, setShowForm] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/studio/photos`);
      setPhotos(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered =
    filter === "todas" ? photos : photos.filter((p) => p.categoria === filter);

  const removePhoto = async (id) => {
    if (!window.confirm("Apagar esta foto?")) return;
    try {
      await axios.delete(`${API}/studio/photos/${id}`);
      load();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <section
      className="px-6 md:px-12 lg:px-24 py-12 md:py-16"
      data-testid="galeria-view"
    >
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-3">
            Galeria · O Estúdio
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter">
            Vê o <span className="text-yellow-400">espaço</span>
          </h2>
          <p className="text-sm text-neutral-400 mt-3 max-w-xl">
            Fotografias reais do estúdio O-meu-Pt.pt — duas zonas de treino, equipamento premium e o ambiente exclusivo onde acontece a tua sessão.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className={`btn-primary inline-flex items-center gap-2 ${authed ? "" : "hidden"}`}
          data-testid="add-studio-photo-btn"
        >
          <Plus size={16} /> Adicionar foto
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const count =
            c.id === "todas"
              ? photos.length
              : photos.filter((p) => p.categoria === c.id).length;
          return (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className="chip"
              data-active={filter === c.id}
              data-testid={`gal-filter-${c.id}`}
            >
              <Icon size={14} /> {c.label}
              {count > 0 ? (
                <span className="ml-1 opacity-60">· {count}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-neutral-800 bg-neutral-950/40 p-12 text-center">
          <ImageIcon size={36} className="mx-auto text-neutral-700 mb-3" />
          <div className="text-neutral-400 mb-2">
            {photos.length === 0
              ? "Sem fotografias ainda."
              : "Sem fotografias nesta categoria."}
          </div>
          {authed ? (
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary mt-4 inline-flex items-center gap-2"
            >
              <Plus size={14} /> Adicionar primeira foto
            </button>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="relative group cursor-pointer border border-neutral-900 hover:border-yellow-400/60 overflow-hidden bg-neutral-950"
              data-testid={`gal-photo-${p.id}`}
              onClick={() => setLightbox(p)}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={p.foto}
                  alt={p.titulo || "Foto do estúdio"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              {(p.titulo || p.categoria) && (
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/95 to-transparent">
                  {p.titulo ? (
                    <div className="text-sm font-bold uppercase tracking-wide truncate">
                      {p.titulo}
                    </div>
                  ) : null}
                  {p.categoria ? (
                    <div className="text-[10px] uppercase tracking-widest text-yellow-400 mt-0.5">
                      {p.categoria}
                    </div>
                  ) : null}
                </div>
              )}
              {authed ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removePhoto(p.id);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 bg-black/80 border border-neutral-700 flex items-center justify-center text-neutral-300 hover:text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`gal-del-${p.id}`}
                >
                  <Trash2 size={14} />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <UploadModal onClose={() => setShowForm(false)} onSaved={load} />
      ) : null}

      {lightbox ? (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="gal-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-white hover:text-yellow-400"
          >
            <X size={32} />
          </button>
          <img
            src={lightbox.foto}
            alt={lightbox.titulo || "Foto"}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {(lightbox.titulo || lightbox.categoria) ? (
            <div className="absolute bottom-6 left-6 right-6 text-center">
              {lightbox.titulo ? (
                <div className="font-display text-2xl uppercase tracking-tight">
                  {lightbox.titulo}
                </div>
              ) : null}
              {lightbox.categoria ? (
                <div className="text-xs uppercase tracking-widest text-yellow-400 mt-1">
                  {lightbox.categoria}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
